import { useState, useEffect } from 'react';
import { Plus, Trash2, Save, AlertCircle, Check, Copy, Eye, ChevronDown, ChevronRight, Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../lib/apiClient';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES, computeChanges } from '../lib/auditLog';
import { ConfirmModal } from './ConfirmModal';
import type { AppTheme, AppColors, ReportColors } from '../types/theme';
import { DEFAULT_APP_COLORS, APP_COLOR_FIELDS, REPORT_COLOR_FIELDS } from '../types/theme';
import { DEFAULT_REPORT_STYLE } from './report/types';
import {
  fetchAllThemes,
  activateTheme,
  createTheme,
  updateTheme,
  deleteTheme,
  applyThemeToDOM,
  storeThemeSlug,
} from '../services/themeService';

interface BrandSettingsLite {
  id: string;
  company_name: string | null;
  logo_url: string | null;
}

function logoBlobPath(logoUrl: string | null): string | null {
  if (!logoUrl) return null;
  const segments = logoUrl.split('/').filter(Boolean);
  if (segments.length === 0) return null;
  const idx = segments.indexOf('company-logos');
  if (idx >= 0 && idx < segments.length - 1) return segments.slice(idx + 1).join('/');
  return segments[segments.length - 1];
}

interface ThemeEditorProps {
  theme: AppTheme;
  onSave: (theme: AppTheme) => void;
  onCancel: () => void;
  isNew: boolean;
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-10 rounded border border-line cursor-pointer flex-shrink-0"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-2 py-1 bg-surface text-theme-primary border border-line rounded text-xs font-mono focus:ring-1 focus:ring-blue-500 focus:border-transparent"
      />
      <span className="text-xs text-theme-secondary w-28 truncate" title={label}>{label}</span>
    </div>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-line rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-subtle hover:bg-subtle transition-colors text-sm font-medium text-theme-primary"
      >
        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        {title}
      </button>
      {isOpen && <div className="p-4">{children}</div>}
    </div>
  );
}

function ThemeEditor({ theme, onSave, onCancel, isNew }: ThemeEditorProps) {
  const { t } = useTranslation('common');
  const [name, setName] = useState(theme.name);
  const [appColors, setAppColors] = useState<AppColors>(theme.app_colors);
  const [reportColors, setReportColors] = useState<ReportColors>(theme.report_colors);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleAppColorChange = (field: keyof AppColors, value: string) => {
    setAppColors((prev) => ({ ...prev, [field]: value }));
  };

  const handleReportColorChange = (field: keyof ReportColors, value: string) => {
    setReportColors((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('themes.nameRequired'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

      if (isNew) {
        const created = await createTheme({
          name: name.trim(),
          slug,
          app_colors: appColors,
          report_colors: reportColors,
        });

        logAudit({
          action: AUDIT_ACTIONS.CREATE,
          entityType: AUDIT_ENTITIES.BRAND_SETTINGS,
          entityId: created.id,
          entityLabel: `Theme: ${created.name}`,
          details: { theme_name: created.name, slug: created.slug },
          pageContext: 'theme-management',
        });

        onSave(created);
      } else {
        await updateTheme(theme.id, {
          name: name.trim(),
          app_colors: appColors,
          report_colors: reportColors,
        });

        const before: Record<string, unknown> = {
          name: theme.name,
          app_colors: theme.app_colors,
          report_colors: theme.report_colors,
        };
        const after: Record<string, unknown> = {
          name: name.trim(),
          app_colors: appColors,
          report_colors: reportColors,
        };
        logAudit({
          action: AUDIT_ACTIONS.UPDATE,
          entityType: AUDIT_ENTITIES.BRAND_SETTINGS,
          entityId: theme.id,
          entityLabel: `Theme: ${name.trim()}`,
          details: computeChanges(before, after),
          pageContext: 'theme-management',
        });

        onSave({
          ...theme,
          name: name.trim(),
          app_colors: appColors,
          report_colors: reportColors,
        });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t('themes.duplicateName'));
      } else {
        setError(t('themes.saveFailed'));
      }
      console.error('Save theme error:', err);
    } finally {
      setSaving(false);
    }
  };

  const appColorGroups = APP_COLOR_FIELDS.reduce((acc, field) => {
    if (!acc[field.group]) acc[field.group] = [];
    acc[field.group].push(field);
    return acc;
  }, {} as Record<string, typeof APP_COLOR_FIELDS>);

  const reportColorGroups = REPORT_COLOR_FIELDS.reduce((acc, field) => {
    if (!acc[field.group]) acc[field.group] = [];
    acc[field.group].push(field);
    return acc;
  }, {} as Record<string, typeof REPORT_COLOR_FIELDS>);

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-theme-primary mb-1">
          {t('themes.themeName')}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={theme.is_builtin}
          className="w-full max-w-sm px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-subtle disabled:text-theme-secondary"
          placeholder={t('themes.namePlaceholder')}
        />
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 rounded-lg text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <CollapsibleSection title={t('themes.appColors')} defaultOpen={true}>
        <div className="space-y-4">
          {Object.entries(appColorGroups).map(([groupName, fields]) => (
            <div key={groupName}>
              <h4 className="text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-2">
                {groupName}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {fields.map((f) => (
                  <ColorField
                    key={f.field}
                    label={f.label}
                    value={appColors[f.field]}
                    onChange={(v) => handleAppColorChange(f.field, v)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title={t('themes.reportColors')}>
        <div className="space-y-4">
          {Object.entries(reportColorGroups).map(([groupName, fields]) => (
            <div key={groupName}>
              <h4 className="text-xs font-semibold text-theme-secondary uppercase tracking-wider mb-2">
                {groupName}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {fields.map((f) => (
                  <ColorField
                    key={f.field}
                    label={f.label}
                    value={reportColors[f.field]}
                    onChange={(v) => handleReportColorChange(f.field, v)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <ThemePreview appColors={appColors} reportColors={reportColors} />

      <div className="flex justify-end gap-3 pt-4 border-t border-line">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-theme-primary hover:bg-subtle rounded-lg transition-colors"
          disabled={saving}
        >
          {t('actions.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400"
        >
          <Save size={18} />
          {saving ? t('status.saving') : t('actions.save')}
        </button>
      </div>
    </div>
  );
}

function ThemePreview({
  appColors,
  reportColors,
}: {
  appColors: AppColors;
  reportColors: ReportColors;
}) {
  const { t } = useTranslation('common');

  return (
    <CollapsibleSection title={t('themes.preview')}>
      <div className="space-y-4">
        <div>
          <h4 className="text-xs font-semibold text-theme-secondary uppercase mb-2">{t('themes.appPreview')}</h4>
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: appColors.border_color }}>
            <div
              className="px-4 py-2 flex items-center justify-between"
              style={{ backgroundColor: appColors.header_bg_color, color: appColors.header_text_color }}
            >
              <span className="font-semibold text-sm">Company Name</span>
              <span className="text-xs" style={{ color: appColors.text_secondary_color }}>user@email.com</span>
            </div>
            <div className="flex">
              <div
                className="w-36 p-3 space-y-1 border-r"
                style={{
                  backgroundColor: appColors.menu_bg_color,
                  color: appColors.menu_text_color,
                  borderColor: appColors.border_color,
                }}
              >
                <div
                  className="text-xs px-2 py-1 rounded"
                  style={{ backgroundColor: appColors.active_item_bg, color: appColors.active_item_text }}
                >
                  Active Item
                </div>
                <div className="text-xs px-2 py-1">Menu Item</div>
                <div className="text-xs px-2 py-1">Menu Item</div>
              </div>
              <div className="flex-1 p-3" style={{ backgroundColor: appColors.page_bg_color }}>
                <div
                  className="rounded-lg p-3 border"
                  style={{
                    backgroundColor: appColors.card_bg_color,
                    borderColor: appColors.border_color,
                  }}
                >
                  <div className="text-sm font-medium" style={{ color: appColors.text_primary_color }}>
                    Card Content
                  </div>
                  <div className="text-xs mt-1" style={{ color: appColors.text_secondary_color }}>
                    Secondary text
                  </div>
                  <div className="flex gap-2 mt-2">
                    <div
                      className="px-2 py-1 rounded text-white text-xs font-medium"
                      style={{ backgroundColor: appColors.primary_color }}
                    >
                      Primary
                    </div>
                    <div
                      className="px-2 py-1 rounded text-white text-xs font-medium"
                      style={{ backgroundColor: appColors.secondary_color }}
                    >
                      Secondary
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="px-4 py-1.5 text-xs text-center"
              style={{ backgroundColor: appColors.footer_bg_color, color: appColors.footer_text_color }}
            >
              Footer
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold text-theme-secondary uppercase mb-2">{t('themes.reportPreview')}</h4>
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: reportColors.border_color }}>
            <div
              className="px-4 py-2"
              style={{ backgroundColor: reportColors.header_color, color: reportColors.header_text_color }}
            >
              <div className="font-bold text-xs">Inspection Report</div>
              <div className="text-[10px] opacity-80">Company</div>
            </div>
            <div className="px-4 py-2" style={{ backgroundColor: reportColors.body_background_color }}>
              <table className="w-full text-[10px]">
                <tbody>
                  <tr>
                    <td className="py-0.5 font-medium" style={{ color: reportColors.info_label_color }}>Property</td>
                    <td className="py-0.5" style={{ color: reportColors.body_text_color }}>Sample</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              className="flex gap-3 px-4 py-1.5 text-[10px]"
              style={{
                backgroundColor: reportColors.summary_bg_color,
                borderTop: `1px solid ${reportColors.border_color}`,
                borderBottom: `1px solid ${reportColors.border_color}`,
              }}
            >
              <span style={{ color: reportColors.summary_text_color }}>Zones: 12</span>
              <span style={{ color: reportColors.summary_highlight_color }}>Issues: 2</span>
            </div>
            <div className="px-4 py-2" style={{ backgroundColor: reportColors.body_background_color }}>
              <div className="text-xs font-bold mb-1" style={{ color: reportColors.section_heading_color }}>
                Details
              </div>
              <div
                className="rounded overflow-hidden text-[10px]"
                style={{ border: `1px solid ${reportColors.border_color}` }}
              >
                <div className="px-2 py-1" style={{ backgroundColor: reportColors.controller_header_color, color: reportColors.body_text_color }}>
                  Controller 1
                </div>
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: reportColors.table_header_color }}>
                      <th className="px-2 py-0.5 text-left font-semibold" style={{ color: reportColors.table_header_label_color }}>Zone</th>
                      <th className="px-2 py-0.5 text-left font-semibold" style={{ color: reportColors.table_header_label_color }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-2 py-0.5" style={{ color: reportColors.body_text_color }}>1</td>
                      <td className="px-2 py-0.5 font-medium" style={{ color: reportColors.ok_status_color }}>OK</td>
                    </tr>
                    <tr style={{ backgroundColor: reportColors.issue_row_bg_color }}>
                      <td className="px-2 py-0.5" style={{ color: reportColors.body_text_color }}>2</td>
                      <td className="px-2 py-0.5 font-medium" style={{ color: reportColors.issue_status_color }}>Issue</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
}

function CompanySettings() {
  const { t } = useTranslation('common');
  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [originalName, setOriginalName] = useState('');
  const [originalLogo, setOriginalLogo] = useState<string | null>(null);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const row = await apiClient.get<Partial<BrandSettingsLite>>('/api/brand-settings');
      if (row.id) {
        setSettingsId(row.id);
        setCompanyName(row.company_name || '');
        setLogoUrl(row.logo_url ?? null);
        setOriginalName(row.company_name || '');
        setOriginalLogo(row.logo_url ?? null);
      }
    } catch (err) {
      console.error('Error fetching company settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: t('themes.logoInvalidType') });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: t('themes.logoTooLarge') });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const oldPath = logoBlobPath(logoUrl);
      if (oldPath) {
        try {
          await apiClient.del(`/api/uploads/company-logos/${oldPath}`);
        } catch (err) {
          if (!(err instanceof ApiError) || err.status !== 404) {
            console.warn('Failed to delete old logo blob:', err);
          }
        }
      }

      const fileExt = file.name.split('.').pop() || 'png';
      const filename = `company-logo-${Date.now()}.${fileExt}`;
      const result = await apiClient.upload('company-logos', file, { path: filename, filename });
      setLogoUrl(result.url);
    } catch {
      setMessage({ type: 'error', text: t('themes.logoUploadFailed') });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!logoUrl) return;
    try {
      const oldPath = logoBlobPath(logoUrl);
      if (oldPath) {
        try {
          await apiClient.del(`/api/uploads/company-logos/${oldPath}`);
        } catch (err) {
          if (!(err instanceof ApiError) || err.status !== 404) throw err;
        }
      }
      setLogoUrl(null);
    } catch {
      setMessage({ type: 'error', text: t('themes.logoRemoveFailed') });
    }
  };

  const handleSave = async () => {
    if (!settingsId) return;
    setSaving(true);
    setMessage(null);

    try {
      await apiClient.put<BrandSettingsLite>('/api/brand-settings', {
        company_name: companyName,
        logo_url: logoUrl,
      });

      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.BRAND_SETTINGS,
        entityId: settingsId,
        entityLabel: companyName,
        details: computeChanges(
          { company_name: originalName, logo_url: originalLogo },
          { company_name: companyName, logo_url: logoUrl },
        ),
        pageContext: 'theme-management',
      });

      setOriginalName(companyName);
      setOriginalLogo(logoUrl);
      window.dispatchEvent(new CustomEvent('brandSettingsUpdated'));
      setMessage({ type: 'success', text: t('themes.companySaved') });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ type: 'error', text: t('themes.companySaveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = companyName !== originalName || logoUrl !== originalLogo;

  if (loading) return null;

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line p-6 mb-6">
      <h3 className="text-lg font-semibold text-theme-primary mb-1">{t('themes.companyTitle')}</h3>
      <p className="text-sm text-theme-secondary mb-4">{t('themes.companySubtitle')}</p>

      {message && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-500/10 dark:text-green-300 dark:border-green-500/30'
              : 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30'
          }`}
        >
          <AlertCircle size={16} />
          {message.text}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">
            {t('themes.companyName')}
          </label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="w-full max-w-sm px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">
            {t('themes.companyLogo')}
          </label>
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <div className="flex items-center gap-3">
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="h-12 w-auto object-contain border border-line rounded p-1"
                />
                <button
                  onClick={handleRemoveLogo}
                  className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <span className="text-sm text-theme-secondary">{t('themes.noLogo')}</span>
            )}
            <label className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
              <Upload size={16} />
              <span>{uploading ? t('status.saving') : logoUrl ? t('themes.changeLogo') : t('themes.uploadLogo')}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>
          <p className="text-xs text-theme-secondary mt-1">{t('themes.logoHint')}</p>
        </div>

        {hasChanges && (
          <div className="flex justify-end pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 text-sm"
            >
              <Save size={16} />
              {saving ? t('status.saving') : t('actions.save')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ThemeManagement() {
  const { t } = useTranslation('common');
  const [themes, setThemes] = useState<AppTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTheme, setEditingTheme] = useState<AppTheme | null>(null);
  const [isNewTheme, setIsNewTheme] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AppTheme | null>(null);

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      const data = await fetchAllThemes();
      setThemes(data);
    } catch (err) {
      console.error('Failed to load themes:', err);
      setMessage({ type: 'error', text: t('themes.loadFailed') });
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (theme: AppTheme) => {
    if (theme.is_active) return;

    try {
      const previousActive = themes.find((th) => th.is_active);
      await activateTheme(theme.id);
      applyThemeToDOM(theme.app_colors);
      storeThemeSlug(theme.slug);

      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.BRAND_SETTINGS,
        entityId: theme.id,
        entityLabel: `Theme: ${theme.name}`,
        details: {
          previous_theme: previousActive?.name || 'none',
          new_theme: theme.name,
        },
        pageContext: 'theme-management',
      });

      window.dispatchEvent(new CustomEvent('brandSettingsUpdated'));
      window.dispatchEvent(new CustomEvent('themeUpdated'));
      await loadThemes();
      setMessage({ type: 'success', text: t('themes.activated', { name: theme.name }) });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Failed to activate theme:', err);
      setMessage({ type: 'error', text: t('themes.activateFailed') });
    }
  };

  const handleNewTheme = () => {
    setIsNewTheme(true);
    setEditingTheme({
      id: '',
      name: '',
      slug: '',
      is_builtin: false,
      is_active: false,
      app_colors: { ...DEFAULT_APP_COLORS },
      report_colors: { ...DEFAULT_REPORT_STYLE },
      created_by: null,
      created_at: '',
      updated_at: '',
    } as AppTheme);
  };

  const handleDuplicate = (theme: AppTheme) => {
    setIsNewTheme(true);
    setEditingTheme({
      ...theme,
      id: '',
      name: `${theme.name} (Copy)`,
      slug: '',
      is_builtin: false,
      is_active: false,
      created_by: null,
      created_at: '',
      updated_at: '',
    });
  };

  const handleSaveTheme = async (_saved: AppTheme) => {
    setEditingTheme(null);
    setIsNewTheme(false);
    window.dispatchEvent(new CustomEvent('themeUpdated'));
    await loadThemes();
    setMessage({ type: 'success', text: t('themes.saved') });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteTheme(deleteTarget.id);

      logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.BRAND_SETTINGS,
        entityId: deleteTarget.id,
        entityLabel: `Theme: ${deleteTarget.name}`,
        pageContext: 'theme-management',
      });

      window.dispatchEvent(new CustomEvent('themeUpdated'));
      setDeleteTarget(null);
      await loadThemes();
      setMessage({ type: 'success', text: t('themes.deleted') });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Failed to delete theme:', err);
      setMessage({ type: 'error', text: t('themes.deleteFailed') });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-theme-secondary">{t('status.loading')}</div>
      </div>
    );
  }

  if (editingTheme) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-theme-primary">
            {isNewTheme ? t('themes.createTheme') : t('themes.editTheme')}
          </h2>
          <p className="text-theme-secondary mt-1">{t('themes.editorSubtitle')}</p>
        </div>
        <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
          <ThemeEditor
            theme={editingTheme}
            onSave={handleSaveTheme}
            onCancel={() => {
              setEditingTheme(null);
              setIsNewTheme(false);
            }}
            isNew={isNewTheme}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-theme-primary">{t('themes.managementTitle')}</h2>
          <p className="text-theme-secondary mt-1">{t('themes.managementSubtitle')}</p>
        </div>
        <button
          onClick={handleNewTheme}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          {t('themes.createTheme')}
        </button>
      </div>

      {message && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-500/10 dark:text-green-300 dark:border-green-500/30'
              : 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30'
          }`}
        >
          <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
          <span>{message.text}</span>
        </div>
      )}

      <CompanySettings />

      <div className="space-y-4">
        {themes.map((theme) => (
          <div
            key={theme.id}
            className={`bg-surface rounded-lg shadow-sm border p-5 transition-all ${
              theme.is_active ? 'border-blue-300 dark:border-blue-500/30 ring-1 ring-blue-200' : 'border-line'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="flex gap-1 flex-shrink-0">
                {[
                  theme.app_colors.primary_color,
                  theme.app_colors.header_bg_color,
                  theme.app_colors.menu_bg_color,
                  theme.app_colors.page_bg_color,
                  theme.report_colors.header_color,
                ].map((color, i) => (
                  <div
                    key={i}
                    className="w-6 h-10 first:rounded-l-lg last:rounded-r-lg border border-line"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-theme-primary">{theme.name}</h3>
                  {theme.is_builtin && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-subtle text-theme-secondary rounded-full">
                      {t('themes.builtin')}
                    </span>
                  )}
                  {theme.is_active && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 rounded-full flex items-center gap-1">
                      <Check size={12} />
                      {t('themes.defaultBadge')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-theme-secondary mt-0.5">
                  {theme.is_builtin
                    ? t(`themes.${theme.slug}Desc`)
                    : t('themes.customTheme')}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {!theme.is_active && (
                  <button
                    onClick={() => handleActivate(theme)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-300 dark:bg-blue-500/10 dark:hover:bg-blue-500/20 rounded-lg transition-colors"
                  >
                    <Check size={14} />
                    {t('themes.setAsDefault')}
                  </button>
                )}
                <button
                  onClick={() => setEditingTheme(theme)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-theme-primary hover:bg-subtle rounded-lg transition-colors"
                >
                  <Eye size={14} />
                  {theme.is_builtin ? t('actions.view') : t('actions.edit')}
                </button>
                <button
                  onClick={() => handleDuplicate(theme)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-theme-primary hover:bg-subtle rounded-lg transition-colors"
                  title={t('themes.duplicate')}
                >
                  <Copy size={14} />
                </button>
                {!theme.is_builtin && (
                  <button
                    onClick={() => setDeleteTarget(theme)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        title={t('themes.deleteTitle')}
        message={deleteTarget ? t('themes.deleteConfirm', { name: deleteTarget.name }) : ''}
        confirmText={t('actions.delete')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />
    </div>
  );
}
